import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { addCoupon } from 'shared/store/kingdom/privileges'
import type { Coupon } from 'shared/types'
import {
    COUPON_CODES,
    couponOrigin,
    orderPickUpType,
    promotionOriginType
} from 'shared/utils'
import { processCouponNavigation } from 'shared/store/ecommerce/order'
import type { RootState } from 'shared/store/reducers'
import { AlertModal } from '../../../../../common/modals'
import { SelectionScreenModal } from '../../../components/discounts/SelectionScreenModal'
import { Recaptcha } from 'app/common/recaptcha'
import { CoffeeIllustration } from '../../../../../../app-v2/assets/images/CoffeeIllustration'
const { KINGDOM_POINTS } = couponOrigin
const { COMMON } = promotionOriginType

let recaptchaPromise: Promise<string>
let resolvePromise: any
let rejectPromise: any

type FreeHotDrinkModalProps = {
    visible: boolean,
    restaurantId: string,
    onClose: () => void
}

export const FreeHotDrinkModal = ({
    visible,
    restaurantId,
    onClose
}: FreeHotDrinkModalProps) => {
    const dispatch = useDispatch()
    const { promotionsById } = useSelector(
        (state: RootState) => state.ecommerceCatalogState
    )
    const [showCaptcha, setShowCaptcha] = useState(false)
    const [selectionScreenInfo, setSelectionScreenInfo] = useState(undefined)
    const [error, setError] = useState('')

    const initRecaptchaPromise = () => {
        recaptchaPromise = new Promise((resolve, reject) => {
            resolvePromise = resolve
            rejectPromise = reject
        })
    }

    const captureResponseToken = (recaptchaToken: string) => {
        if (!!recaptchaToken) {
            resolvePromise(recaptchaToken)
            return
        }
        rejectPromise('Error getting recaptcha token')
    }

    const validateAndRedirect = async (coupon: Coupon) => {
        const { id: couponCode, promotionId, origin } = coupon || {}
        const isImpersonalOperation = origin === couponOrigin.IMPERSONAL_COUPON
        const promotion = promotionsById[promotionId || '']

        let errorMessage

        if (promotion) {
            const CCpromo = {
                couponCode,
                idReboot: promotion.idReboot,
                promoType: promotion.promoType,
                promoValue: promotion.promoValue || 0,
                promotionId,
                promotionName: promotion.name,
                threshold: promotion.threshold
            }
            const {
                listItems,
                type,
                error,
                requiredType,
                listRequiredItems
            } = await dispatch(processCouponNavigation(coupon, promotion))

            const promotionOrigin =
                origin === KINGDOM_POINTS ? undefined : COMMON

            if (listRequiredItems && requiredType && !error) {
                //$FlowFixMe
                setSelectionScreenInfo({
                    listItems: listRequiredItems,
                    type: requiredType,
                    promotionOrigin,
                    isImpersonalOperation,
                    promotionToApply: CCpromo,
                    coupon
                })
                return
            }

            errorMessage = error

            // it means we need to display the selection screen
            if (listItems && type && !errorMessage) {
                setSelectionScreenInfo({
                    listItems,
                    promotion: CCpromo,
                    type,
                    promotionOrigin,
                    isImpersonalOperation
                })
            }
        } else {
            errorMessage = 'error.section.title'
        }
        setError(errorMessage)
    }

    useEffect(() => {
        initRecaptchaPromise()
        setShowCaptcha(true)
    }, [])

    const addFreeCoupon = async () => {
        try {
            setShowCaptcha(true)
            const recaptchaToken = await recaptchaPromise
            const coupon = await dispatch(
                addCoupon({
                    code: COUPON_CODES.FREE_HOT_DRINK,
                    queen: recaptchaToken,
                    pickUpType: orderPickUpType.TABLE_ORDER,
                    restaurant: restaurantId
                })
            )
            if (coupon) {
                validateAndRedirect(coupon)
            }
        } catch (error) {
            console.log('Error adding coupon from modal', error)
        } finally {
            setShowCaptcha(false)
            initRecaptchaPromise()
        }
    }

    return (
        <>
            <SelectionScreenModal
                selectionScreenInfos={selectionScreenInfo}
                displaySelectionScreen={!!selectionScreenInfo}
                selectedCoupon={undefined}
                handleClose={() => {
                    setSelectionScreenInfo(undefined)
                    onClose()
                }}
            />
            {visible && !selectionScreenInfo && (
                <AlertModal
                    alertTitleKey="clickandcollect.catalog.modal.freeHotDrink.title"
                    alertContentKey="clickandcollect.catalog.modal.freeHotDrink.description"
                    alertMentionKey="clickandcollect.catalog.modal.freeHotDrink.condition"
                    okLabel="yes"
                    leftLabel="no"
                    isOpen={visible && !selectionScreenInfo}
                    illustration={<CoffeeIllustration />}
                    handleClose={onClose}
                    onOkClick={async () => {
                        await addFreeCoupon()
                    }}
                    onLeftClick={onClose}
                    disableClose
                />
            )}
            {showCaptcha && (
                <Recaptcha
                    action="AddFreeHotDrinkCoupon"
                    verifyCallback={captureResponseToken}
                />
            )}
        </>
    )
}
